import { useState, type ComponentProps } from 'react';
import { Puzzle } from 'lucide-react';
import { cn } from '../../lib/utils';

type Size = 'sm' | 'md' | 'lg';

const sizeClasses: Record<Size, { box: string; icon: string; rounded: string }> = {
  sm: { box: 'h-8 w-8', icon: 'h-4 w-4', rounded: 'rounded-md' },
  md: { box: 'h-10 w-10', icon: 'h-5 w-5', rounded: 'rounded-lg' },
  lg: { box: 'h-14 w-14', icon: 'h-7 w-7', rounded: 'rounded-xl' },
};

interface PluginIconProps extends Omit<ComponentProps<'img'>, 'src' | 'onError'> {
  icon?: string | null;
  size?: Size;
  fallbackIcon?: React.ReactNode;
}

export function PluginIcon({
  icon,
  size = 'md',
  fallbackIcon,
  className,
  ...imgProps
}: PluginIconProps) {
  const [failed, setFailed] = useState(false);
  const s = sizeClasses[size];

  if (!icon || failed) {
    return (
      <div
        data-testid="plugin-icon"
        data-size={size}
        className={cn(
          'flex shrink-0 items-center justify-center border border-border/40 bg-muted/30',
          s.box,
          s.rounded,
          className,
        )}
      >
        {fallbackIcon ?? <Puzzle className={cn('text-muted-foreground', s.icon)} />}
      </div>
    );
  }

  return (
    <img
      {...imgProps}
      data-testid="plugin-icon"
      data-size={size}
      src={`/plugin-icons/${icon}`}
      alt={imgProps.alt ?? ''}
      onError={() => setFailed(true)}
      className={cn('shrink-0 border border-border/40 object-cover', s.box, s.rounded, className)}
    />
  );
}
